import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useEffect, useState } from "react";
import Latex from "react-latex-next";

import { ExerciseFooter } from "./components/ExerciseFooter";
import { useAnswerCheck } from "./useAnswerCheck";

import { ABCDOptions, AbilityScore, AnswerExercise, ErrorModal } from "components";
import { ExerciseSelfEvaluation } from "features";

import type { UserAssignmentForExerciseFragment } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getAssignmentResult,
  setAbilityScore,
  setAnswer,
  setAssignmentEvaluation,
  setTimeLearn,
  setTips,
} from "store/slices/exercise";
import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";
import type { IOption } from "store/slices/topicPractice";

import { AssignmentPrompt } from "components/AssignmentPrompt";
import { SuccessRate } from "components/SuccessRate/SuccessRate";
import { changeDistinctAbilityScore, getAbilityScoreOptions } from "utils/changeAbilityScore";
import { ABCDOptionText } from "utils/formatTextForAnalysis";
import { evaluateABCD, getSolutionVariant } from "utils/getSolutionVariant";
import { getTimeToLearn } from "utils/getTimeToLearn";
import { useEmptyAssignmentAnswer } from "utils/hooks/useEmptyAssignmentAnswer";
import { useSaveExerciseSelfEvaluation } from "utils/hooks/useSaveExerciseSelfEvaluation";
import { isAbilityScoreShown } from "utils/isAbilityScoreShown";
import { logGeneralExerciseError } from "utils/processApiError";
import { isSelfEvaluationForEveryAssignment } from "utils/valueTranslators";

/* ky8UIEVUI2t6j78KohBn */

interface Props {
  data: UserAssignmentForExerciseFragment;
  userAssignmentId: string;
  evaluateAnswer: boolean;
  onNextClick: () => void;
}

const ABCD: FC<Props> = ({ data, onNextClick, userAssignmentId, evaluateAnswer }) => {
  const questionsCount = data.assignment?.assignment?.questions.length ?? 0;
  const isSelfEvaluation = data.assignment?.scoring?.scoringMethod?.scoringMethod === "sebeopravení";

  const saveExerciseSelfEvaluation = useSaveExerciseSelfEvaluation();
  const dispatch = useAppDispatch();
  const assignmentResults = useAppSelector(getAssignmentResult<IOption>(userAssignmentId));
  const time = useAppSelector(getAssignmentTime);
  const { setEmptyExerciseAnswer } = useEmptyAssignmentAnswer();

  const { correctAnswers, points, check, isChecking, isErrorModalVisible, hideErrorModal } = useAnswerCheck({
    data,
    userAssignmentId,
    evaluateAnswer,
  });

  const [goNext, setGoNext] = useState(false);

  useEffect(() => {
    dispatch(resetAssignmentTimer());

    if (assignmentResults?.answers === undefined || assignmentResults.answers.length === 0) {
      dispatch(
        setAnswer({
          assignmentId: userAssignmentId,
          answer: new Array<IOption>(questionsCount).fill({ value: undefined }),
        }),
      );
    }

    if (assignmentResults?.tips === undefined || assignmentResults.tips.length === 0) {
      dispatch(setTips({ userAssignmentId, tips: new Array(questionsCount).fill(false) }));
    }

    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    const responseAnswers = correctAnswers?.map(questionAnswers => {
      if (data.assignment?.assignment?.isAnswerCaseSensitive === true) return questionAnswers;

      return questionAnswers
        .filter((answer): answer is string => answer !== null)
        .map(answer => answer.toLocaleLowerCase());
    });

    if (responseAnswers === undefined || responseAnswers.length === 0 || assignmentResults?.answers === undefined) {
      return;
    }

    dispatch(
      setAssignmentEvaluation({
        assignmentId: userAssignmentId,
        status: evaluateABCD(assignmentResults.answers, responseAnswers),
        answerVariants: evaluateAnswers(responseAnswers),
        correctAnswers: responseAnswers,
      }),
    );
  }, [correctAnswers]);

  useEffect(() => {
    if (goNext) {
      dispatch(setTimeLearn({ assignmentId: userAssignmentId, timeLearn: time }));
      onNextClick();
      setGoNext(false);
    }
  }, [goNext]);

  const evaluateAnswers = (answers: Array<Array<string | null>>) => {
    if (data.assignment?.assignment === undefined || data.assignment?.assignment === null) return [];
    const caseSensitive = data.assignment.assignment.isAnswerCaseSensitive;

    return data.assignment.assignment.questions.map((_question, qIndex) =>
      getSolutionVariant(assignmentResults?.answers?.[qIndex].value, answers, qIndex, caseSensitive ?? false),
    );
  };

  const onSubmit = () => {
    check(assignmentResults?.answers?.map(answer => ({ answers: [answer.value ?? ""] })));
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    setGoNext(true);
  };

  const handleCheck = (index: number, option: IOption) => {
    if (assignmentResults?.answers === undefined) return;

    const copy = [...assignmentResults.answers];
    copy[index] = option;
    dispatch(setAnswer({ assignmentId: userAssignmentId, answer: copy }));
  };

  const areAnswersFilled = () => {
    try {
      const answers = assignmentResults?.answers;
      const areFilled =
        answers !== undefined &&
        answers.length > 0 &&
        answers.every(answer => typeof answer.value === "string" && answer.value.length > 0);

      return areFilled;
    } catch (error) {
      logGeneralExerciseError(error, data?.assignment?.id, assignmentResults?.answers);
      return false;
    }
  };

  const handleAbilityScoreSelect = (value: number, index: number) => {
    if (
      data.assignment === undefined ||
      data.assignment === null ||
      !isSelfEvaluationForEveryAssignment(data.assignment)
    )
      return;

    const newAbilityScore = changeDistinctAbilityScore(value, index, assignmentResults?.abilityScore, questionsCount);
    dispatch(setAbilityScore({ assignmentId: userAssignmentId, score: newAbilityScore }));
  };

  const getAbilityScoreOnIndex = (index: number) => {
    const abilityScore = assignmentResults?.abilityScore;

    if (abilityScore === undefined || typeof abilityScore === "number") return 0;

    return abilityScore[index];
  };

  if (isSelfEvaluation) {
    if (data.assignment === undefined || data.assignment === null) return null;

    return (
      <ExerciseSelfEvaluation
        userAssignment={data}
        userAssignmentId={userAssignmentId}
        results={assignmentResults}
        onNextClick={handleNextClick}
        saveAnswer={saveExerciseSelfEvaluation}
        evaluateAnswer={evaluateAnswer}
      />
    );
  }

  return (
    <div className="exercise__body">
      <AssignmentPrompt assignment={data.assignment?.assignment} />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => {
          if (assignmentResults?.correctAnswers === undefined) {
            return (
              <div key={qIndex}>
                <Latex>{question.heading ?? ""}</Latex>
                <ABCDOptions
                  key={qIndex}
                  options={question.options}
                  qIndex={qIndex}
                  value={assignmentResults?.answers?.[qIndex]}
                  onClick={handleCheck}
                />
              </div>
            );
          }

          const selfEvalOptions = getAbilityScoreOptions(
            isSelfEvaluation,
            assignmentResults?.answersVariants?.[qIndex],
          );

          return (
            <div key={qIndex}>
              <Latex>{question.heading ?? ""}</Latex>
              <AnswerExercise
                subject={data.subject?.appName}
                key={`answer-${qIndex}`}
                index={qIndex}
                assignment={data.assignment}
                userAssignmentId={data.id}
                type={assignmentResults?.answersVariants?.[qIndex] ?? "danger"}
                timeToLearn={getTimeToLearn(data.timeToLearn)}
                onNext={handleNextClick}
              >
                {assignmentResults === undefined ? "" : ABCDOptionText(assignmentResults.answers?.[qIndex])}
              </AnswerExercise>

              {typeof data.assignment?.successRate === "number" && (
                <div className="question mt-32px">
                  <SuccessRate successRate={data.assignment?.successRate} withHr={false} />
                </div>
              )}

              {data.assignment !== undefined &&
              data.assignment !== null &&
              isSelfEvaluationForEveryAssignment(data.assignment) &&
              question.showAbilityScore === true &&
              selfEvalOptions !== null ? (
                <>
                  <hr className={"my-25px"} />
                  <div className="mw-sm">
                    <AbilityScore
                      isSelfEvaluated={isSelfEvaluation}
                      options={selfEvalOptions}
                      selectedValue={getAbilityScoreOnIndex(qIndex)}
                      onValueSelect={(value: number) => {
                        handleAbilityScoreSelect(value, qIndex);
                      }}
                      index={qIndex}
                    />
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
        <ErrorModal
          onCancel={() => {
            setEmptyExerciseAnswer(userAssignmentId, evaluateAnswers);
            hideErrorModal();
          }}
          onHide={() => {
            hideErrorModal();
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        <ExerciseFooter
          onSubmit={onSubmit}
          isSubmitDisabled={!areAnswersFilled() || isChecking}
          isAnalysis={assignmentResults?.correctAnswers !== undefined}
          goNext={handleNextClick}
          userAssignmentId={userAssignmentId}
          showAbilityScore={isAbilityScoreShown(data.assignment?.scoring?.selfEvaluation?.selfEvaluation)}
          points={points}
          maxPoints={points === null ? null : 1}
        />
      </div>
    </div>
  );
};

export default ABCD;
